import type { UploadFile } from "antd"
import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router"

import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { validateFile } from "../../../../../../utils/file-validation.ts"
import { getAttachments } from "../../../../../Album/redux/attachments.thunks.ts"
import { AttachmentModels } from "../../../../../Album/types/attachment.types.ts"
import { uploadProjectAttachment } from "../../../../redux/project-attachments/project-attachments.thunks.ts"
import { AlbumFilesSettings } from "../../../constants/album-files-settings.tsx"
import { mapAttachmentsToFileList } from "../../../helpers/map-attachments-to-file-list.ts"
import { ProjectRecentFiles } from "./ProjectRecentFiles/ProjectRecentFiles.tsx"

const RECENT_FILES_LIMIT = 10

interface ProjectViewAlbumsRecentFilesSectionProps {
  uploadAllowed: boolean
}

export function ProjectViewAlbumsRecentFilesSection({
  uploadAllowed
}: ProjectViewAlbumsRecentFilesSectionProps) {
  const projectId = useParams()?.id as string
  const dispatch = useAppDispatch()

  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [loading, setLoading] = useState(false)
  // const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  const fetchAttachments = useCallback(async () => {
    try {
      setLoading(true)
      const result = await dispatch(
        getAttachments({
          project: projectId,
          page_size: RECENT_FILES_LIMIT,
          ordering: "-created"
        })
      ).unwrap()

      const attachments = result?.slice(0, RECENT_FILES_LIMIT) || []
      setFileList(mapAttachmentsToFileList(attachments))
    } catch (e) {
      console.error("Failed to fetch attachments:", e)
    } finally {
      setLoading(false)
    }
  }, [dispatch, projectId])

  useEffect(() => {
    void fetchAttachments()
  }, [fetchAttachments])

  const handleUpload = useCallback(
    async (file: File) => {
      const uid = crypto.randomUUID()
      const abortController = new AbortController()

      // abortControllersRef.current.set(uid, abortController)

      setFileList((prev) => [
        {
          uid,
          name: file.name,
          status: "uploading",
          percent: 0
        },
        ...prev
      ])

      try {
        const res = await dispatch(
          uploadProjectAttachment({
            data: {
              model_type: AttachmentModels.PROJECT,
              object_id: projectId,
              project_id: projectId,
              file
            },
            signal: abortController.signal
          })
        ).unwrap()

        // abortControllersRef.current.delete(uid)

        setFileList((prev) =>
          prev.map((f) =>
            f.uid === uid
              ? {
                  ...f,
                  uid: res.id,
                  status: "done",
                  url: res.file,
                  size: res.file_size,
                  type: res.mime_type,
                  response: res
                }
              : f
          )
        )
      } catch (e) {
        // abortControllersRef.current.delete(uid)

        if (abortController.signal.aborted) {
          setFileList((prev) => prev.filter((f) => f.uid !== uid))
          return
        }

        setFileList((prev) =>
          prev.map((f) =>
            f.uid === uid
              ? {
                  ...f,
                  status: "error"
                }
              : f
          )
        )
      }
    },
    [dispatch, projectId]
  )

  // const handleCancelUpload = useCallback((uid: string) => {
  //   const abortController = abortControllersRef.current.get(uid)
  //   if (abortController) {
  //     abortController.abort()
  //     abortControllersRef.current.delete(uid)
  //   }
  // }, [])

  const handleBeforeUpload = useCallback(
    (file: File) => {
      const result = validateFile(file, {
        maxSize: AlbumFilesSettings.MAX_SIZE_MB,
        allowedExtensions: AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS,
        defaultResult: true
      })

      if (result === true) {
        void handleUpload(file)
      }

      return false
    },
    [handleUpload]
  )

  return (
    <ProjectRecentFiles
      projectId={projectId}
      fileList={fileList}
      uploadAllowed={uploadAllowed}
      loading={loading}
      maxFileSize={AlbumFilesSettings.MAX_SIZE_MB}
      allowedExtensions={AlbumFilesSettings.ALLOWED_FILE_EXTENSIONS}
      recentFilesLimit={RECENT_FILES_LIMIT}
      onBeforeUpload={handleBeforeUpload}
      // onCancelUpload={handleCancelUpload}
    />
  )
}
